/**
 * Data table component with CCMS SDK integration
 */

import { forwardRef, useState } from 'react';
import { Table, Stack } from '@/components/ui';
import { CCMSLoadingState } from '../../shared/components/CCMSLoadingState';
import { CCMSErrorState } from '../../shared/components/CCMSErrorState';
import { CCMSEmptyState } from '../../shared/components/CCMSEmptyState';
import type { CCMSDataTableProps, TableColumn } from '../types';

export const CCMSDataTable = forwardRef<HTMLDivElement, CCMSDataTableProps>(
  (props, ref) => {
    const {
      data,
      columns,
      loading = false,
      error = null,
      emptyMessage = 'No data found',
      sortable = true,
      onSort,
      onRowClick,
      striped = true,
      highlightOnHover = true,
      className,
    } = props;

    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const handleSort = (column: TableColumn) => {
      if (!sortable || !column.sortable) return;

      const direction = sortConfig?.key === column.key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
      const newConfig: { key: string; direction: 'asc' | 'desc' } = { key: column.key, direction };
      
      setSortConfig(newConfig);
      onSort?.({ field: column.key, direction });
    };

    if (loading) {
      return <CCMSLoadingState message="Loading data..." />;
    }

    if (error) {
      return <CCMSErrorState error={error} title="Failed to load data" />;
    }

    if (!data || data.length === 0) {
      return <CCMSEmptyState title={emptyMessage} />;
    }

    return (
      <Stack gap="md" ref={ref} className={className}>
        <Table tableVariant={striped ? 'striped' : 'default'}>
          <Table.Thead>
            <Table.Tr>
              {columns.map((column) => (
                <Table.Th
                  key={column.key}
                  style={{
                    width: column.width,
                    textAlign: column.align || 'left',
                    cursor: sortable && column.sortable ? 'pointer' : 'default',
                  }}
                  onClick={() => sortable && column.sortable && handleSort(column)}
                >
                  {column.header}
                  {sortConfig?.key === column.key && (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map((row, rowIndex) => (
              <Table.Tr
                key={rowIndex}
                onClick={() => onRowClick?.(row)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((column) => {
                  const value = typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor];
                  return (
                    <Table.Td key={column.key} style={{ textAlign: column.align || 'left' }}>
                      {column.render ? column.render(value, row) : String(value || '')}
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    );
  }
);

CCMSDataTable.displayName = 'CCMSDataTable';
export default CCMSDataTable;
